import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function TopDeskIntegratie() {
  const { gebruiker } = useAuth();
  const [koppeling, setKoppeling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [log, setLog] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [activeTab, setActiveTab] = useState('instellingen');
  const [mappings, setMappings] = useState([]);
  const [filters, setFilters] = useState([]);
  const [form, setForm] = useState({
    topdesk_url: '',
    api_gebruiker: '',
    api_token: '',
    sync_interval: 5,
    sync_meldingen_naar_werkorders: true,
    sync_status_terugkoppelen: true,
    sync_werkbon_terugsturen: false,
  });

useEffect(() => { loadKoppeling(); loadFilters(); }, []);

  const loadKoppeling = async () => {
    try {
      setLoading(true);
      const res = await api.get('/topdesk/koppeling');
      if (res.data.koppeling) {
        setKoppeling(res.data.koppeling);
        setForm({
          topdesk_url: res.data.koppeling.topdesk_url || '',
          api_gebruiker: res.data.koppeling.api_gebruiker || '',
          api_token: '', // Never show token
          sync_interval: res.data.koppeling.sync_interval || 5,
          sync_meldingen_naar_werkorders: res.data.koppeling.sync_meldingen_naar_werkorders,
          sync_status_terugkoppelen: res.data.koppeling.sync_status_terugkoppelen,
          sync_werkbon_terugsturen: res.data.koppeling.sync_werkbon_terugsturen,
        });
      }
    } catch (e) {
      console.error('Fout bij laden:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async () => {
    try {
      const res = await api.get('/topdesk/log?limit=100');
      setLog(res.data.log || []);
    } catch (e) { console.error(e); }
  };

  const loadIncidents = async () => {
    try {
      setLoadingIncidents(true);
      const res = await api.get('/topdesk/incidents');
      setIncidents(res.data.incidents || []);
    } catch (e) { 
      console.error(e);
      alert('Fout bij ophalen incidents. Is de koppeling geconfigureerd?');
    } finally {
      setLoadingIncidents(false);
    }
  };

const loadFilters = async () => {
    try {
      const res = await api.get('/topdesk/filters');
      setFilters(res.data.filters || []);
    } catch (e) { console.error(e); }
  };

  const loadMappings = async () => {
    try {
      const res = await api.get('/topdesk/mappings');
      setMappings(res.data.mappings || []);
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    if (!form.topdesk_url || !form.api_gebruiker) {
      alert('Vul de URL en gebruikersnaam in');
      return;
    }
    if (!koppeling && !form.api_token) {
      alert('Vul het API token in');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.api_token && koppeling) {
        // Stuur bestaand token mee als er geen nieuw is ingevuld
        payload.api_token = '__KEEP__';
      }
      await api.post('/topdesk/koppeling', payload);
      setTestResult(null);
      setSyncResult(null);
      await loadKoppeling();
      alert('Koppeling opgeslagen!');
    } catch (e) {
      alert('Fout bij opslaan: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        topdesk_url: form.topdesk_url,
        api_gebruiker: form.api_gebruiker,
        api_token: form.api_token || (koppeling ? '__USE_SAVED__' : ''),
      };
      const res = await api.post('/topdesk/test', payload);
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.message || 'Verbinding mislukt' });
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/topdesk/sync');
      setSyncResult(res.data);
      loadLog();
      loadMappings();
    } catch (e) {
      setSyncResult({ success: false, message: e.response?.data?.message || 'Sync mislukt' });
    } finally {
      setSyncing(false);
    }
  };

  const handleStartAutoSync = async () => {
    try {
      await api.post('/topdesk/sync/start');
      await loadKoppeling();
      alert('Auto-sync gestart!');
    } catch (e) { alert('Fout: ' + e.message); }
  };

  const handleStopAutoSync = async () => {
    try {
      await api.post('/topdesk/sync/stop');
      await loadKoppeling();
      alert('Auto-sync gestopt');
    } catch (e) { alert('Fout: ' + e.message); }
  };

  const tabs = [
    { id: 'instellingen', label: '⚙️ Instellingen', icon: '⚙️' },
    { id: 'incidents', label: '📋 TOPdesk Incidents', icon: '📋' },
    { id: 'mappings', label: '🔗 Koppelingen', icon: '🔗' },
    { id: 'log', label: '📊 Sync Log', icon: '📊' },
  ];

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>Laden...</div>;

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #dc2626 100%)', padding: '24px 28px', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }}>🔌</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>TOPdesk Integratie</h1>
            <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: 14 }}>
              Synchroniseer meldingen automatisch met WOMsys
              {koppeling && <span style={{ marginLeft: 12, background: koppeling.sync_actief ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.2)', padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>
                {koppeling.sync_actief ? '🟢 Actief' : '⏸ Gepauzeerd'}
              </span>}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 20px' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => {
            setActiveTab(tab.id);
            if (tab.id === 'log') loadLog();
            if (tab.id === 'incidents') loadIncidents();
            if (tab.id === 'mappings') loadMappings();
          }}
            style={{ padding: '14px 20px', border: 'none', borderBottom: activeTab === tab.id ? '3px solid #2563eb' : '3px solid transparent', background: 'none', color: activeTab === tab.id ? '#2563eb' : '#6b7280', fontWeight: activeTab === tab.id ? 600 : 400, cursor: 'pointer', fontSize: 14 }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>

        {/* ═══ INSTELLINGEN TAB ═══ */}
        {activeTab === 'instellingen' && (
          <>
            {/* Connectie form */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>🔗 Verbinding</h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#374151' }}>TOPdesk URL *</label>
                  <input value={form.topdesk_url} onChange={e => setForm({ ...form, topdesk_url: e.target.value })} placeholder="https://jouwbedrijf.topdesk.net"
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#374151' }}>API Gebruiker *</label>
                  <input value={form.api_gebruiker} onChange={e => setForm({ ...form, api_gebruiker: e.target.value })} placeholder="womsys-api"
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#374151' }}>
                  Application Password {koppeling ? '(laat leeg om huidige te behouden)' : '*'}
                </label>
                <input type="password" value={form.api_token} onChange={e => setForm({ ...form, api_token: e.target.value })} placeholder={koppeling ? '••••••••••••••••' : 'Plak hier je application password'}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={handleTest} disabled={testing || !form.topdesk_url || !form.api_gebruiker}
                  style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontWeight: 500, fontSize: 14, opacity: testing ? 0.6 : 1 }}>
                  {testing ? '⏳ Testen...' : '🔌 Verbinding Testen'}
                </button>
                <button onClick={handleSave} disabled={saving}
                  style={{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Opslaan...' : '💾 Opslaan'}
                </button>
                {testResult && (
                  <span style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, background: testResult.success ? '#ecfdf5' : '#fef2f2', color: testResult.success ? '#059669' : '#dc2626', border: `1px solid ${testResult.success ? '#a7f3d0' : '#fecaca'}` }}>
                    {testResult.success ? '✅ ' : '❌ '}{testResult.message}
                  </span>
                )}
              </div>
            </div>

            {/* Sync instellingen */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>🔄 Synchronisatie</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>Meldingen → Werkorders</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Automatisch werkorders aanmaken van TOPdesk meldingen</div>
                  </div>
                  <button onClick={() => setForm({ ...form, sync_meldingen_naar_werkorders: !form.sync_meldingen_naar_werkorders })}
                    style={{ width: 48, height: 26, borderRadius: 13, border: 'none', background: form.sync_meldingen_naar_werkorders ? '#10b981' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form.sync_meldingen_naar_werkorders ? 25 : 3, transition: 'left 0.2s' }} />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>Status terugkoppelen</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Werkorder status automatisch terugmelden naar TOPdesk</div>
                  </div>
                  <button onClick={() => setForm({ ...form, sync_status_terugkoppelen: !form.sync_status_terugkoppelen })}
                    style={{ width: 48, height: 26, borderRadius: 13, border: 'none', background: form.sync_status_terugkoppelen ? '#10b981' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form.sync_status_terugkoppelen ? 25 : 3, transition: 'left 0.2s' }} />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>Werkbon PDF terugsturen</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>PDF werkbon als bijlage toevoegen aan TOPdesk melding</div>
                  </div>
                  <button onClick={() => setForm({ ...form, sync_werkbon_terugsturen: !form.sync_werkbon_terugsturen })}
                    style={{ width: 48, height: 26, borderRadius: 13, border: 'none', background: form.sync_werkbon_terugsturen ? '#10b981' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form.sync_werkbon_terugsturen ? 25 : 3, transition: 'left 0.2s' }} />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>Sync interval</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Hoe vaak controleren op nieuwe meldingen</div>
                  </div>
                  <select value={form.sync_interval} onChange={e => setForm({ ...form, sync_interval: parseInt(e.target.value) })}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    <option value={1}>Elke minuut</option>
                    <option value={2}>Elke 2 minuten</option>
                    <option value={5}>Elke 5 minuten</option>
                    <option value={10}>Elke 10 minuten</option>
                    <option value={15}>Elke 15 minuten</option>
                    <option value={30}>Elke 30 minuten</option>
                  </select>
                </div>
              </div>
            </div>
             {/* Afdeling Filters */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>🎯 Afdeling Filters</h2>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>Bepaal welke TOPdesk meldingen geïmporteerd worden. Alleen meldingen die matchen worden werkorders.</p>
              
              {filters.map(f => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, marginBottom: 6 }}>
                  <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: '#eff6ff', color: '#2563eb' }}>{f.filter_veld}</span>
                  <span style={{ fontSize: 13, color: '#374151' }}>bevat <strong>"{f.filter_waarde}"</strong></span>
                  {f.afdeling_naam && <span style={{ fontSize: 12, color: '#6b7280' }}>→ {f.afdeling_naam}</span>}
                  <button onClick={async () => { await api.delete('/topdesk/filters/' + f.id); loadFilters(); }} style={{ marginLeft: 'auto', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              ))}
              {filters.length === 0 && <div style={{ padding: 12, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Geen filters — alle meldingen worden geïmporteerd</div>}

              
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', padding: '16px', background: '#f9fafb', borderRadius: 8, marginTop: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Filter op veld</label>
                  <select id="filterVeld" style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    <option value="category">Categorie</option>
                    <option value="subcategory">Subcategorie</option>
                    <option value="callType">Type melding</option>
                    <option value="operatorGroup">Operator groep</option>
                    <option value="callerBranch">Branch / Vestiging</option>
                    <option value="department">Afdeling (melder)</option>
                    <option value="priority">Prioriteit</option>
                    <option value="briefDescription">Omschrijving (bevat)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Waarde</label>
                  <input id="filterWaarde" placeholder="Bijv. IT Services" style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 200 }} />
                </div>
                <button onClick={async () => { const v = document.getElementById('filterVeld').value; const w = document.getElementById('filterWaarde').value; if (!w) { alert('Vul een waarde in'); return; } try { await api.post('/topdesk/filters', { filter_veld: v, filter_waarde: w }); document.getElementById('filterWaarde').value = ''; loadFilters(); } catch(e) { alert('Fout'); } }}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                  + Toevoegen
                </button>
              </div>
            </div>




            {/* Acties */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>⚡ Acties</h2>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={handleSync} disabled={syncing || !koppeling}
                  style={{ padding: '12px 24px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: (syncing || !koppeling) ? 0.6 : 1 }}>
                  {syncing ? '⏳ Synchroniseren...' : '🔄 Nu Synchroniseren'}
                </button>
                {koppeling && !koppeling.sync_actief && (
                  <button onClick={handleStartAutoSync} style={{ padding: '12px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    ▶ Auto-sync Starten
                  </button>
                )}
                {koppeling && koppeling.sync_actief && (
                  <button onClick={handleStopAutoSync} style={{ padding: '12px 24px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    ⏸ Auto-sync Stoppen
                  </button>
                )}
              </div>

              {syncResult && (
                <div style={{ marginTop: 16, padding: 16, borderRadius: 8, background: syncResult.success ? '#ecfdf5' : '#fef2f2', border: `1px solid ${syncResult.success ? '#a7f3d0' : '#fecaca'}` }}>
                  {syncResult.success ? (
                    <div>
                      <div style={{ fontWeight: 600, color: '#059669', marginBottom: 6 }}>✅ Synchronisatie voltooid!</div>
                      <div style={{ fontSize: 13, color: '#374151' }}>
                        {syncResult.created > 0 && <span style={{ marginRight: 12 }}>📥 {syncResult.created} nieuwe werkorders</span>}
                        {syncResult.skipped > 0 && <span style={{ marginRight: 12 }}>⏭ {syncResult.skipped} al gesynchroniseerd</span>}
                        {syncResult.errors > 0 && <span style={{ color: '#dc2626' }}>❌ {syncResult.errors} fouten</span>}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#dc2626', fontWeight: 500 }}>❌ {syncResult.message}</div>
                  )}
                </div>
              )}

              {koppeling?.laatste_sync && (
                <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                  Laatste sync: {new Date(koppeling.laatste_sync).toLocaleString('nl-NL')}
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══ INCIDENTS TAB ═══ */}
        {activeTab === 'incidents' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>📋 Live TOPdesk Incidents</h2>
              <button onClick={loadIncidents} disabled={loadingIncidents}
                style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                {loadingIncidents ? '⏳ Laden...' : '🔄 Vernieuwen'}
              </button>
            </div>

            {incidents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                {loadingIncidents ? 'Incidents laden...' : 'Geen incidents gevonden. Klik op Vernieuwen.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incidents.map(inc => (
                  <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', minWidth: 90 }}>{inc.number}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>{inc.briefDescription || 'Geen beschrijving'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {inc.caller} — {inc.branch}
                        {inc.priority && <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, fontSize: 11, background: inc.priority === 'P1' ? '#fef2f2' : '#f3f4f6', color: inc.priority === 'P1' ? '#dc2626' : '#6b7280' }}>{inc.priority}</span>}
                      </div>
                    </div>
                    <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: inc.isSynced ? '#ecfdf5' : '#fef3c7', color: inc.isSynced ? '#059669' : '#92400e' }}>
                      {inc.isSynced ? '✅ Gesynchroniseerd' : '⏳ Nieuw'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ MAPPINGS TAB ═══ */}
        {activeTab === 'mappings' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>🔗 TOPdesk ↔ WOMsys Koppelingen</h2>
            {mappings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Nog geen koppelingen. Synchroniseer eerst.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {mappings.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f9fafb', borderRadius: 8 }}>
                    <div style={{ minWidth: 90 }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>TOPdesk</div>
                      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{m.topdesk_nummer}</div>
                    </div>
                    <span style={{ color: '#9ca3af' }}>→</span>
                    <div style={{ minWidth: 90 }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>WOMsys</div>
                      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{m.werkorder_nummer}</div>
                    </div>
                    <div style={{ flex: 1, fontSize: 13, color: '#374151' }}>{m.beschrijving}</div>
                    <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: m.werkorder_status === 'afgerond' ? '#ecfdf5' : m.werkorder_status === 'open' ? '#eff6ff' : '#f3f4f6', color: m.werkorder_status === 'afgerond' ? '#059669' : m.werkorder_status === 'open' ? '#2563eb' : '#6b7280' }}>
                      {m.werkorder_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ LOG TAB ═══ */}
        {activeTab === 'log' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>📊 Sync Log</h2>
              <button onClick={loadLog} style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>🔄 Vernieuwen</button>
            </div>
            {log.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Nog geen sync activiteit.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {log.map(entry => (
                  <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: entry.status === 'error' ? '#fef2f2' : '#f9fafb', borderRadius: 6, fontSize: 13 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af', minWidth: 130 }}>{new Date(entry.created_at).toLocaleString('nl-NL')}</span>
                    <span style={{ fontSize: 14 }}>{entry.richting === 'topdesk_naar_womsys' ? '📥' : '📤'}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, minWidth: 80, color: '#6b7280' }}>{entry.topdesk_nummer}</span>
                    <span style={{ fontSize: 14 }}>{entry.status === 'success' ? '✅' : '❌'}</span>
                    <span style={{ flex: 1, color: '#374151' }}>{entry.actie}</span>
                    {entry.werkorder_nummer && <span style={{ fontSize: 12, color: '#2563eb' }}>→ {entry.werkorder_nummer}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TopDeskIntegratie;
